import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Accordion, Form, SearchInput } from '@qodo/design-system';
import {
  GitLabGroup,
  GitlabWebhookInstallResult,
} from 'network/httpClients/GitlabClient';
import { useInstallGitlabWebhooksByGroupsMutation } from 'network/mutations/useInstallGitlabWebhooksByGroupsMutation';
import { useInstallGitlabWebhooksMutation } from 'network/mutations/useInstallGitlabWebhooksMutation';
import { useLoginGitlabRetry } from 'network/mutations/useLoginGitlabRetry';
import { useGitlabGroupsQuery } from 'network/queries/useGitlabGroupsQuery';
import { QUERY_PARAMS } from 'routes/consts';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { useDebounceValue } from 'usehooks-ts';
import { spawn } from 'utils/translations';
import { Loader } from '../../Loader';
import {
  initialSelectionState,
  selectionReducer,
} from '../connectRepositoriesReducer';
import {
  getEffectiveGroupIds,
  getGroupDepth,
  getRootGroup,
  isDescendantOf,
  mergeWebhookResults,
} from '../connectRepositoriesUtils';
import {
  FORM_IDS,
  GITLAB_CALLBACK_STATUS,
  GITLAB_ERROR_CODES,
  OAUTH_CALLBACK_PARAMS,
  SELECTION_ACTIONS,
  STEP_IDS,
} from '../consts';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { GitlabErrorCode } from '../types';
import { GroupSection } from './parts/GroupSection';
import { ProvisionGroupsResults } from './ProvisionGroupsResults';

export const ConnectRepositories = () => {
  const [oauthError, setOauthError] = useState<string>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300);
  const [installedIds, setInstalledIds] = useState<Set<number>>(new Set());
  const [searchParams] = useSearchParams();
  const [installationId, setInstallationId] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [isMultiProvision, setIsMultiProvision] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [groupsInstalled, setGroupsInstalled] = useState(0);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [
    { selectedIds, selectedGroupIds, expandedGroups, groupProjectRegistry },
    dispatch,
  ] = useReducer(selectionReducer, initialSelectionState);
  const hasInitializedGroupSelection = useRef(false);

  const navigate = useNavigate();
  const methods = useForm();
  const {
    appConfigId,
    setAppConfigId,
    setWebhookResult,
    setTotalSucceededCount,
  } = useGitlabInstallation();
  const {
    goNext,
    onComplete,
    setCurrentStepAsFurthestStepReached,
    setEarliestStepAvailable,
    setIsNextEnabled,
    setIsPending,
    setStepHidden,
    currentStepIndex,
    steps,
  } = useMultiStepDialog();

  const {
    data: groups,
    isLoading: isLoadingGroups,
    isFetching: isFetchingGroups,
    isError: isGroupsError,
    refetch: refetchGroups,
  } = useGitlabGroupsQuery(appConfigId!, { enabled: !!appConfigId });

  const rootGroup = getRootGroup(groups ?? []);
  const isRootSelected = rootGroup ? selectedGroupIds.has(rootGroup.id) : false;

  const { mutate: loginGitlabRetry } = useLoginGitlabRetry();
  const webhookMutation = useInstallGitlabWebhooksMutation();
  const webhooksByGroupsMutation = useInstallGitlabWebhooksByGroupsMutation();

  const spawnStep = spawn('dialogs.gitlab-installation.connect-repositories');
  const spawnErrors = spawn('dialogs.gitlab-installation.errors');

  useEffect(() => {
    const status = searchParams.get(QUERY_PARAMS.STATUS);
    const errorCode = searchParams.get(QUERY_PARAMS.ERROR_CODE);
    const appConfigIdParam = searchParams.get(QUERY_PARAMS.APP_CONFIG_ID);
    const installationIdParam = searchParams.get(QUERY_PARAMS.INSTALLATION_ID);

    if (status === 'error' && errorCode) {
      const isValidErrorCode = Object.values(GITLAB_ERROR_CODES).includes(
        errorCode as GitlabErrorCode,
      );
      const errorMessage = isValidErrorCode
        ? spawnErrors(errorCode)
        : spawnErrors('UNEXPECTED_ERROR');

      unlockStepAllowingBack();
      setOauthError(errorMessage);
      stripOAuthCallbackParams();

      return;
    }

    // Multi-group (B-v1a): the callback stashed a provision session (no
    // app_config_id yet). Render the provision-results step, which calls
    // provision-groups to create the installs.
    if (status === GITLAB_CALLBACK_STATUS.AWAITING_PROVISION) {
      unlockCurrentStep();
      setIsMultiProvision(true);
      // Multi-group is terminal at this step — drop the single-install
      // CheckConnection/EnableQodo steps from the sidebar so its count matches.
      setStepHidden(STEP_IDS.CHECK_CONNECTION, true);
      setStepHidden(STEP_IDS.ENABLE_QODO, true);
      stripOAuthCallbackParams();

      return;
    }

    if (appConfigIdParam && installationIdParam) {
      unlockCurrentStep();
      setAppConfigId(appConfigIdParam);
      setInstallationId(installationIdParam);
      stripOAuthCallbackParams();

      return;
    }

    navigate(steps[0].path, { replace: true });
  }, []);

  useEffect(() => {
    if (!groups?.length) return;

    if (hasInitializedGroupSelection.current) return;

    hasInitializedGroupSelection.current = true;
    dispatch({
      type: SELECTION_ACTIONS.SELECT_ALL_GROUPS,
      ids: groups.map((g) => g.id),
    });
  }, [groups]);

  useEffect(() => {
    if (!groups?.length) return;

    if (debouncedSearchQuery) {
      dispatch({
        type: SELECTION_ACTIONS.SET_EXPANDED_GROUPS,
        names: groups.map((g) => g.fullPath),
      });
    } else {
      dispatch({
        type: SELECTION_ACTIONS.SET_EXPANDED_GROUPS,
        names: [],
      });
    }
  }, [debouncedSearchQuery, groups]);

  const effectiveGroupIds = getEffectiveGroupIds(
    selectedGroupIds,
    groups ?? [],
  );
  const effectiveGroupIdSet = new Set(effectiveGroupIds);
  const wholeGroupRepoCount = (groups ?? []).reduce(
    (sum, group) =>
      effectiveGroupIdSet.has(group.id)
        ? sum + (group.repositoriesCount ?? 0)
        : sum,
    0,
  );
  const totalSelectedCount = wholeGroupRepoCount + selectedIds.size;

  useEffect(() => {
    const hasNewReposSelected = Array.from(selectedIds).some(
      (id) => !installedIds.has(id),
    );
    const hasGroupsSelected = selectedGroupIds.size > 0;

    setIsNextEnabled(hasNewReposSelected || hasGroupsSelected);
  }, [selectedIds, selectedGroupIds, installedIds]);

  const onInstalledIdsLoaded = useCallback((newInstalledIds: number[]) => {
    setInstalledIds((prev) => {
      const next = new Set(prev);

      newInstalledIds.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const unlockCurrentStep = () => {
    setCurrentStepAsFurthestStepReached();
    setEarliestStepAvailable(currentStepIndex);
  };

  // On a callback ERROR we keep this step rendered (it shows the failure) but
  // must NOT lock the navigation floor to it — otherwise Back greys out (the
  // footer disables Back when earliestStepAvailable >= currentStepIndex) and
  // the user is trapped on the error with only a same-request Retry. Drop the
  // floor back to "Configure Qodo" so Back walks there to fix the group URL /
  // credentials and re-run. (The success / awaiting-provision returns keep the
  // floor at the current step — going back into a consumed OAuth flow is wrong.)
  const unlockStepAllowingBack = () => {
    setCurrentStepAsFurthestStepReached();
    const configureStepIndex = steps.findIndex(
      (step) => step.id === STEP_IDS.CONFIGURE_APP,
    );

    setEarliestStepAvailable(configureStepIndex >= 0 ? configureStepIndex : 0);
  };

  const stripOAuthCallbackParams = () => {
    const cleanParams = new URLSearchParams(searchParams.toString());

    OAUTH_CALLBACK_PARAMS.forEach((param) => cleanParams.delete(param));
    const cleanSearch = cleanParams.toString();

    navigate(
      { search: cleanSearch ? `?${cleanSearch}` : '' },
      { replace: true },
    );
  };

  const onRetry = () => {
    setIsRetrying(true);
    loginGitlabRetry(undefined, {
      onSuccess: (response) => {
        navigate(response.authorizationUrl);
      },
      onError: () => {
        setIsRetrying(false);
      },
    });
  };

  const onInstallWebhooks = async () => {
    if (!appConfigId || !installationId) return;

    const groupIds = effectiveGroupIds;
    const groupCoveredIds = new Set<number>();

    groupIds.forEach((groupId) => {
      groupProjectRegistry
        .get(groupId)
        ?.forEach((id) => groupCoveredIds.add(id));
    });

    const newProjectIds = Array.from(selectedIds).filter(
      (id) => !installedIds.has(id) && !groupCoveredIds.has(id),
    );

    setIsPending(true);
    setIsInstalling(true);
    setGroupsTotal(groupIds.length);
    setGroupsInstalled(0);

    try {
      // Install each group in parallel (rather than one batched call) so we can
      // tick determinate "X of N groups connected" progress as each resolves.
      // Group installs hit GitLab per project and can take minutes for large
      // groups (the Restaurant365 case). Promise.all keeps the original "any
      // hard failure → catch" semantics; per-project failures still flow
      // through each group's result as before.
      const groupResultsPromise = Promise.all(
        groupIds.map(async (groupId) => {
          const result = await webhooksByGroupsMutation.mutateAsync({
            appConfigId,
            groupIds: [groupId],
          });

          setGroupsInstalled((prev) => prev + 1);

          return result;
        }),
      );

      const projectsPromise: Promise<GitlabWebhookInstallResult> =
        newProjectIds.length > 0
          ? webhookMutation.mutateAsync({
              appConfigId,
              projectIds: newProjectIds,
            })
          : Promise.resolve({ succeeded: [], failed: [] });

      const [projectsResult, groupResults] = await Promise.all([
        projectsPromise,
        groupResultsPromise,
      ]);

      const groupsResult = groupResults.reduce<GitlabWebhookInstallResult>(
        (acc, result) => mergeWebhookResults(acc, result),
        { succeeded: [], failed: [] },
      );

      const combined = mergeWebhookResults(projectsResult, groupsResult);

      setWebhookResult(combined);
      setTotalSucceededCount(combined.succeeded.length);
      goNext();
    } catch (error) {
      console.error('Failed to install GitLab webhooks', error);
      setIsPending(false);
      setIsInstalling(false);
    }
  };

  const renderGroupSection = (group: GitLabGroup) => {
    const depth = rootGroup ? getGroupDepth(group, rootGroup) : 0;
    // A subgroup is covered when the selected root is its ancestor — the root's
    // whole-subtree install already includes it.
    const isCoveredByRoot =
      isRootSelected && !!rootGroup && isDescendantOf(group, rootGroup);

    return (
      <GroupSection
        key={group.id}
        group={group}
        appConfigId={appConfigId!}
        searchQuery={debouncedSearchQuery}
        isExpanded={expandedGroups.has(group.fullPath)}
        selectedIds={selectedIds}
        selectedGroupIds={selectedGroupIds}
        installedIds={installedIds}
        dispatch={dispatch}
        onInstalledIdsLoaded={onInstalledIdsLoaded}
        depth={depth}
        isCoveredByRoot={isCoveredByRoot}
        coveredByName={rootGroup?.name}
      />
    );
  };

  const renderAccordionContent = () => {
    if (isGroupsError) {
      return (
        <InstallationStatusMessage
          status="error"
          message={spawnStep('fetch-error')}
          retryLabel={spawnStep('retry-button')}
          onRetry={refetchGroups}
          isRetrying={isFetchingGroups}
        />
      );
    }

    if (isLoadingGroups) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader className="size-6" />
        </div>
      );
    }

    if (!groups || groups.length === 0) {
      return (
        <p className="text-sm font-medium py-4">
          {spawnStep('no-repositories')}
        </p>
      );
    }

    return (
      <Accordion
        type="multiple"
        variant="filled"
        value={Array.from(expandedGroups)}
        onValueChange={(names) =>
          dispatch({
            type: SELECTION_ACTIONS.SET_EXPANDED_GROUPS,
            names,
          })
        }
      >
        {groups.map(renderGroupSection)}
      </Accordion>
    );
  };

  const renderFormContent = () => {
    if (oauthError) {
      return (
        <InstallationStatusMessage
          status="error"
          message={spawnStep('error')}
          detail={oauthError}
          retryLabel={spawnStep('retry-button')}
          onRetry={onRetry}
          isRetrying={isRetrying}
        />
      );
    }

    // While webhooks install we replace the selection list with a determinate
    // progress view. Group installs hit GitLab per project and can take a few
    // minutes for large groups (the Restaurant365 case) — the bare button
    // spinner alone read as "stuck".
    if (isInstalling) {
      const groupProgressPercent =
        groupsTotal > 0 ? Math.round((groupsInstalled / groupsTotal) * 100) : 0;

      return (
        <div className="flex flex-col items-center justify-center gap-4 flex-1 py-12 text-center">
          <Loader className="size-6" />
          <p className="text-sm text-muted-foreground max-w-85">
            {spawnStep('installing', { count: totalSelectedCount })}
          </p>
          {groupsTotal > 0 && (
            <div className="flex flex-col items-center gap-2 w-60">
              <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${groupProgressPercent}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {spawnStep('installing-groups-progress', {
                  done: groupsInstalled,
                  total: groupsTotal,
                })}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        <p className="text-sm text-muted-foreground">
          {spawnStep('group-note')}
        </p>
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {!isLoadingGroups &&
              spawnStep('selected', { count: totalSelectedCount })}
          </span>
          <div className="min-w-82.5 relative">
            <SearchInput
              placeholder={spawnStep('search-placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isLoadingGroups}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto -mr-10 pr-10">
          {renderAccordionContent()}
        </div>
      </div>
    );
  };

  if (isMultiProvision) {
    // Multi-group is terminal: the footer's primary button completes the wizard
    // (→ Repositories) rather than advancing into the single-install
    // CheckConnection/EnableQodo steps. Binding the step's form to onComplete
    // makes that Next button actually submit.
    return (
      <Step title={spawnStep('title')} description={spawnStep('description')}>
        <Form
          {...({ id: FORM_IDS.CONNECT_REPOSITORIES } as Record<string, string>)}
          methods={methods}
          onSubmit={onComplete}
          className="flex flex-col flex-1 min-h-0"
        >
          <ProvisionGroupsResults />
        </Form>
      </Step>
    );
  }

  return (
    <Step title={spawnStep('title')} description={spawnStep('description')}>
      <Form
        {...({ id: FORM_IDS.CONNECT_REPOSITORIES } as Record<string, string>)}
        methods={methods}
        onSubmit={onInstallWebhooks}
        className="flex flex-col flex-1 min-h-0"
      >
        {renderFormContent()}
      </Form>
    </Step>
  );
};
